/**
 * Does the left rail cover what the user just selected? (Logo Initiative LG-1)
 *
 * The rail is a floating island, not an in-flow panel: `builder.css` shifts the
 * canvas right by only HALF the rail's width — "so the floating sidebar overlays
 * it without hiding the centered content". That bet is that page content is
 * centred, so the overlapped strip is empty margin. It holds for body sections
 * and fails completely for a full-bleed header, whose logo is pinned to the left
 * edge. Measured: 452px rail, 226px canvas shift, **222px of live canvas covered
 * at every desktop width**, which is exactly where every template puts its logo.
 *
 * Widening the shift was considered and rejected: the canvas is TRANSLATED, not
 * resized, so a bigger shift only trades left-edge occlusion for right-edge
 * clipping. Yielding the rail is the cheaper, reversible answer.
 *
 * This module is pure geometry so it can be unit-tested without a browser. The
 * decision to act on it lives in `shell.js`, which owns rail state.
 */

/** Ignore sub-pixel and hairline overlaps — they are not what hides a logo. */
const MEANINGFUL_OVERLAP_PX = 8;

function normalizeRect(rect) {
  if (!rect) return null;
  const left = Number(rect.left ?? rect.x);
  const top = Number(rect.top ?? rect.y);
  const width = Number(rect.width);
  const height = Number(rect.height);
  if (![left, top, width, height].every(Number.isFinite)) return null;
  if (width <= 0 || height <= 0) return null;
  return { left, top, right: left + width, bottom: top + height, width, height };
}

/**
 * Is `targetRect` meaningfully covered by `railRect`?
 *
 * Both rects must be in the SAME coordinate space — viewport coordinates from
 * `getBoundingClientRect()` is the only space where the rail and an
 * iframe-measured element can be compared, so the caller is responsible for
 * having already offset the element rect by the preview frame's own position.
 *
 * Returns false for a rail that is not actually painting (zero-sized, or moved
 * off-screen by the collapse transform), so a collapsed rail never asks to
 * collapse again.
 *
 * @param {{left?:number,x?:number,top?:number,y?:number,width:number,height:number}|null} targetRect
 * @param {{left?:number,x?:number,top?:number,y?:number,width:number,height:number}|null} railRect
 * @returns {boolean}
 */
export function isRectOccludedByRail(targetRect, railRect) {
  const target = normalizeRect(targetRect);
  const rail = normalizeRect(railRect);
  if (!target || !rail) return false;
  // A collapsed rail sits at a negative x, entirely left of the viewport.
  if (rail.right <= 0) return false;

  const overlapX = Math.min(target.right, rail.right) - Math.max(target.left, rail.left);
  const overlapY = Math.min(target.bottom, rail.bottom) - Math.max(target.top, rail.top);
  if (overlapX <= 0 || overlapY <= 0) return false;

  // Require a real bite out of the target, not a grazing edge: either axis must
  // clear the threshold AND the covered area must be a visible share of it.
  if (overlapX < MEANINGFUL_OVERLAP_PX || overlapY < MEANINGFUL_OVERLAP_PX) return false;
  const coveredShare = (overlapX * overlapY) / (target.width * target.height);
  return coveredShare >= 0.2;
}

/**
 * Viewport-space rect of an element measured INSIDE the preview iframe.
 *
 * The iframe reports rects in its own document space; adding the frame element's
 * viewport position is what makes them comparable with parent chrome. Returns
 * null when either half is unavailable rather than guessing.
 */
export function toViewportRect(iframeEl, innerRect) {
  const inner = normalizeRect(innerRect);
  if (!inner || !iframeEl?.getBoundingClientRect) return null;
  const frame = iframeEl.getBoundingClientRect();
  return {
    left: frame.left + inner.left,
    top: frame.top + inner.top,
    width: inner.width,
    height: inner.height
  };
}
